import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  marketsOverviewApi,
  type CompanyInMarket,
  type MarketOverview,
} from "../api/marketsOverview";
import type { ApiError } from "../api/client";
import { Pager } from "../components/Pager";
import { TableSkeleton } from "../components/Loading";
import { flagEmoji } from "../i18n/countries";
import { formatNumber } from "../lib/format";

/**
 * Region-level overview for the Sales Director / Admin "Markets" page.
 *
 * <p>Two-level drill-down — click a market to expand its country breakdown
 * and the people covering it; click a country to load the companies in that
 * country on demand. Companies link to the existing admin company detail
 * page so the director can dive straight to the customer.
 */
export default function MarketsOverviewPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<MarketOverview[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openMarket, setOpenMarket] = useState<string | null>(null);
  const [openCountry, setOpenCountry] = useState<string | null>(null);
  const [companies, setCompanies] = useState<CompanyInMarket[] | null>(null);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  // Strana i ukupan broj kupaca u zemlji. Srbija ih ima 11.483, pa se zemlja ne čita cela: ekran je
  // pre ovoga tražio sve redove i po dva upita za svaki, a tabela od jedanaest hiljada redova se ne
  // čita nego se zatvara.
  const [companiesPage, setCompaniesPage] = useState(0);
  const [companiesTotal, setCompaniesTotal] = useState(0);
  const VELICINA = 25;

  useEffect(() => {
    marketsOverviewApi
      .overview()
      .then(setData)
      .catch((e: ApiError) => setError(e.message));
  }, []);

  async function openCountryDrilldown(code: string, page = 0) {
    setOpenCountry(code);
    setCompaniesPage(page);
    // Pri prelasku na drugu stranu zatečeni redovi ostaju na ekranu, a kostur ide samo kad još ničega
    // nema — inače tabela trepne pri svakom kliku i čitalac izgubi mesto na kom je bio.
    if (page === 0) setCompanies(null);
    setCompaniesLoading(true);
    try {
      const strana = await marketsOverviewApi.companiesInCountry(code, page, VELICINA);
      setCompanies(strana.content);
      setCompaniesTotal(strana.totalElements);
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setCompaniesLoading(false);
    }
  }

  function toggleMarket(id: string) {
    if (openMarket === id) {
      setOpenMarket(null);
      setOpenCountry(null);
      setCompanies(null);
      setCompaniesPage(0);
      return;
    }
    setOpenMarket(id);
    setOpenCountry(null);
    setCompanies(null);
    setCompaniesPage(0);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>{t("markets.title", "Markets")}</h1>
        <p style={{ margin: "4px 0 0", color: "var(--ink-3)", fontSize: 13 }}>
          {t("markets.subtitle",
            "Regional roll-up: companies, coverage, invoiced orders and KPI targets per market. Click a market to expand, then click a country to list its customers.")}
        </p>
      </header>

      {error && <div style={errorBox}>{error}</div>}

      {!data ? (
        <p style={{ color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</p>
      ) : data.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("markets.empty", "No markets configured yet.")}</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {data.map((m) => {
            const open = openMarket === m.id;
            return (
              <div key={m.id} style={cardOuter}>
                <button type="button" onClick={() => toggleMarket(m.id)} style={cardHeader}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, textAlign: "left" }}>
                    <strong style={{ fontSize: 16, color: "var(--brand-ink)" }}>{m.name}</strong>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {m.countryCodes.map((cc) => (
                        <span key={cc} style={chip}>{flagEmoji(cc)} {cc}</span>
                      ))}
                    </div>
                    {m.description && (
                      <p style={{ margin: "4px 0 0", color: "var(--ink-3)", fontSize: 12 }}>{m.description}</p>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
                    <Stat label={t("markets.col.companies", "Companies")} value={String(m.companyCount)} />
                    <Stat label={t("markets.col.reps", "Sales reps")} value={String(m.salesRepCount)} />
                    <Stat label={t("markets.col.invoiced", "Invoiced YTD")} value={m.invoicedOrdersYtd == null ? "—" : String(m.invoicedOrdersYtd)} accent="var(--ok)" />
                    <Stat label={t("markets.col.target", "Target YTD")} value={m.kpiTargetYtdEur == null ? "—" : `€ ${formatEur(Number(m.kpiTargetYtdEur))}`} accent="var(--brand-ink)" />
                    <span style={{ fontSize: 18, color: "var(--ink-4)", marginLeft: 8 }}>{open ? "▾" : "▸"}</span>
                  </div>
                </button>

                {open && (
                  <div style={{ borderTop: "1px solid var(--border)", padding: 16, display: "flex", flexDirection: "column", gap: 18 }}>
                    <section>
                      <h3 style={sectionHeader}>{t("markets.section.reps", "Coverage")}</h3>
                      {m.salesReps.length === 0 ? (
                        <p style={{ margin: 0, color: "var(--ink-4)", fontSize: 13 }}>
                          {t("markets.noReps", "No sales reps assigned to this market yet.")}
                        </p>
                      ) : (
                        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
                          {m.salesReps.map((rep) => (
                            <li key={rep.userId} style={repRow}>
                              <div>
                                <strong style={{ color: "var(--brand-ink)" }}>{rep.name}</strong>
                                <a href={`mailto:${rep.email}`} style={{ color: "var(--brand-ink)", fontSize: 12, marginLeft: 8, textDecoration: "none" }}>{rep.email}</a>
                              </div>
                              <span style={chip}>
                                {rep.coverage === "MARKET"
                                  ? t("markets.coverage.whole", "Whole market")
                                  : rep.countries.map((c) => `${flagEmoji(c)} ${c}`).join(", ")}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>

                    <section>
                      <h3 style={sectionHeader}>{t("markets.section.countries", "Countries")}</h3>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
                        {m.countries.map((c) => {
                          const active = openCountry === c.countryCode;
                          return (
                            <button
                              key={c.countryCode}
                              type="button"
                              onClick={() => openCountryDrilldown(c.countryCode)}
                              style={{ ...countryTile, borderColor: active ? "var(--brand-primary)" : "var(--border)", boxShadow: active ? "0 0 0 2px var(--brand-primary-50)" : "none" }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ fontWeight: 600, color: "var(--ink)" }}>{flagEmoji(c.countryCode)} {c.countryCode}</span>
                                <span style={{ fontSize: 11, color: "var(--ink-4)" }}>{c.companyCount} {t("markets.companiesShort", "companies")}</span>
                              </div>
                              <div style={{ marginTop: 6, fontSize: 11, color: "var(--ok)" }}>
                                {c.invoicedOrdersYtd ?? "—"} {t("markets.invoicedShort", "invoiced YTD")}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </section>

                    {openCountry && (
                      <section>
                        <h3 style={sectionHeader}>
                          {t("markets.section.customers", "Customers in")} {flagEmoji(openCountry)} {openCountry}
                        </h3>
                        {companiesLoading && !companies ? (
                          <TableSkeleton rows={6} cols={4} />
                        ) : !companies || companies.length === 0 ? (
                          <p style={{ margin: 0, color: "var(--ink-4)", fontSize: 13 }}>{t("markets.noCustomers", "No customer companies in this country.")}</p>
                        ) : (
                          <table style={tbl}>
                            <thead>
                              <tr>
                                <th style={th}>{t("markets.customers.name", "Company")}</th>
                                <th style={th}>{t("markets.customers.city", "City")}</th>
                                <th style={th}>{t("markets.customers.primaryRep", "Primary sales rep")}</th>
                                <th style={{ ...th, textAlign: "right" }}>{t("markets.customers.invoiced", "Invoiced YTD")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {companies.map((c) => (
                                <tr key={c.id}>
                                  <td style={td}>
                                    <Link to={`/admin/companies/${c.id}`} style={{ color: "var(--brand-ink)", textDecoration: "none", fontWeight: 600 }}>
                                      {c.name}
                                    </Link>
                                  </td>
                                  <td style={td}>{c.city ?? "—"}</td>
                                  <td style={td}>{c.primarySalesName ?? <span style={{ color: "var(--ink-4)" }}>—</span>}</td>
                                  <td style={{ ...td, textAlign: "right" }}>{c.invoicedOrdersYtd ?? "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        {companies && companies.length > 0 && (
                          <Pager
                            page={companiesPage}
                            size={VELICINA}
                            total={companiesTotal}
                            busy={companiesLoading}
                            onPage={(p) => void openCountryDrilldown(openCountry, p)}
                          />
                        )}
                      </section>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: "var(--ink-4)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: accent ?? "var(--ink)", marginTop: 2 }}>{value}</div>
    </div>
  );
}

function formatEur(n: number): string {
  if (!Number.isFinite(n) || n === 0) return "0";
  return formatNumber(n, undefined, { maximumFractionDigits: 0 });
}

const cardOuter: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, boxShadow: "var(--shadow-1)", overflow: "hidden" };
const cardHeader: React.CSSProperties = { width: "100%", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, padding: 18, background: "transparent", border: "none", cursor: "pointer", textAlign: "left" };
const chip: React.CSSProperties = { background: "var(--brand-primary-50)", color: "var(--brand-ink)", fontSize: 11, padding: "3px 8px", borderRadius: 999, fontWeight: 600, letterSpacing: 0.3 };
const sectionHeader: React.CSSProperties = { margin: "0 0 8px", fontSize: 12, color: "var(--ink-2)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 };
const repRow: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", background: "var(--surface-2)", borderRadius: 6, border: "1px solid var(--border)" };
const countryTile: React.CSSProperties = { padding: 10, borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)", cursor: "pointer", textAlign: "left", transition: "border-color .12s ease, box-shadow .12s ease" };
const tbl: React.CSSProperties = { borderCollapse: "collapse", width: "100%", fontSize: 13, background: "var(--surface)" };
const th: React.CSSProperties = { textAlign: "left", padding: "8px 10px", background: "var(--surface-2)", color: "var(--ink-2)", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "8px 10px", borderBottom: "1px solid var(--border)" };
const errorBox: React.CSSProperties = { padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
